import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

/*
 * WorkspaceContext — tracks which workspace the user is currently looking
 * at (Personal vs an Organization they belong to) and transparently injects
 * `org_id` into outbound `/api/tasks` calls so the backend can scope its
 * response correctly.
 *
 * The provider:
 *   1. Loads `/api/feature-flags` + `/api/organizations/mine` once the user
 *      is signed in (and lazily refreshes on demand).
 *   2. Persists the active workspace selection in localStorage under
 *      `activeWorkspaceId` ('personal' or the org id).
 *   3. Installs a single axios request interceptor that adds `?org_id=` to
 *      GET /api/tasks and merges `org_id` into POST /api/tasks bodies when
 *      the active workspace is an org. Personal mode is unchanged so the
 *      rollout is fully backward-compatible.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'activeWorkspaceId';
const PERSONAL = { kind: 'personal', id: 'personal', name: 'Personal' };

const WorkspaceContext = createContext({
  activeWorkspace: PERSONAL,
  workspaces: [PERSONAL],
  switchWorkspace: () => {},
  refreshWorkspaces: () => Promise.resolve(),
  isBusinessEnabled: false,
});

// Module-scoped ref so the axios interceptor can read the current
// workspace without React-state staleness. Updated in lockstep with the
// provider's state.
const liveActiveOrgId = { current: null };

let interceptorInstalled = false;
const installAxiosInterceptor = () => {
  if (interceptorInstalled) return;
  interceptorInstalled = true;
  axios.interceptors.request.use((config) => {
    try {
      const url = (config.url || '').toString();
      const orgIdForContacts = liveActiveOrgId.current;
      // Contacts are scoped per workspace: every /api/contacts call carries the active org_id.
      if (orgIdForContacts && /\/api\/contacts(\/|\?|$)/.test(url)) {
        if (!(config.params && 'org_id' in config.params)) {
          config.params = { ...(config.params || {}), org_id: orgIdForContacts };
        }
        return config;
      }
      const isTaskCall = /\/api\/tasks(\b|\/|\?)/.test(url);
      if (!isTaskCall) return config;
      const orgId = liveActiveOrgId.current;
      if (!orgId) return config; // personal mode — unchanged behavior
      const method = (config.method || 'get').toUpperCase();
      if (method === 'GET' || method === 'DELETE') {
        config.params = { ...(config.params || {}), org_id: orgId };
      } else if (method === 'POST' || method === 'PUT' || method === 'PATCH') {
        // Only inject for the create endpoint — PUT/PATCH on /tasks/:id
        // should keep whatever org_id the existing task has.
        const isCreate = /\/api\/tasks(\?|$)/.test(url);
        if (isCreate && config.data && typeof config.data === 'object') {
          if (config.data.org_id === undefined) {
            config.data = { ...config.data, org_id: orgId };
          }
        }
      }
    } catch { /* never let the interceptor break a request */ }
    return config;
  });
};

// Install the interceptor + seed the ref at module import time so the
// very first /api/tasks call after a page reload is already scoped to
// the persisted active workspace — no waiting for the React tree to
// mount first.
if (typeof window !== 'undefined') {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && stored !== 'personal') {
      liveActiveOrgId.current = stored;
    }
  } catch { /* noop */ }
  installAxiosInterceptor();
}

export const WorkspaceProvider = ({ user, children }) => {
  const [orgs, setOrgs] = useState([]);
  const [orgsLoaded, setOrgsLoaded] = useState(false);
  const [isBusinessEnabled, setIsBusinessEnabled] = useState(false);
  const [activeId, setActiveId] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || 'personal'; } catch { return 'personal'; }
  });
  const loadedForUserRef = useRef(null);

  // Keep the axios interceptor's view of the active org id in sync.
  useEffect(() => {
    liveActiveOrgId.current = activeId && activeId !== 'personal' ? activeId : null;
  }, [activeId]);

  const refreshWorkspaces = useCallback(async () => {
    if (!user) {
      setOrgs([]);
      setIsBusinessEnabled(false);
      setOrgsLoaded(true);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const flagsRes = await axios.get(`${API}/feature-flags`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const enabled = !!flagsRes.data?.business_workspaces;
      setIsBusinessEnabled(enabled);
      if (!enabled) {
        setOrgs([]);
        setOrgsLoaded(true);
        return;
      }
      const res = await axios.get(`${API}/organizations/mine`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrgs(res.data || []);
    } catch (e) {
      // Silent — switcher just falls back to Personal-only if the API is unreachable.
      setOrgs([]);
      setIsBusinessEnabled(false);
    } finally {
      setOrgsLoaded(true);
    }
  }, [user]);

  useEffect(() => {
    installAxiosInterceptor();
    const uid = user?.id || null;
    if (loadedForUserRef.current === uid) return;
    loadedForUserRef.current = uid;
    setOrgsLoaded(false);
    refreshWorkspaces();
  }, [user, refreshWorkspaces]);

  const workspaces = useMemo(() => {
    const list = [PERSONAL];
    for (const o of orgs) {
      list.push({ kind: 'org', id: o.id, name: o.name, role: o.my_role, raw: o });
    }
    return list;
  }, [orgs]);

  const activeWorkspace = useMemo(() => {
    if (activeId === 'personal') return PERSONAL;
    const found = workspaces.find((w) => w.id === activeId);
    return found || PERSONAL;
  }, [activeId, workspaces]);

  const switchWorkspace = useCallback((w) => {
    const id = typeof w === 'string' ? w : (w?.id || 'personal');
    // Update the ref synchronously BEFORE notifying listeners — the axios
    // interceptor reads from this ref, and we need it to be correct by the
    // time the workspace-changed event handler calls fetchTasks().
    liveActiveOrgId.current = id && id !== 'personal' ? id : null;
    setActiveId(id);
    try { localStorage.setItem(STORAGE_KEY, id); } catch { /* noop */ }
    // Hint listeners (notably the tasks page) to refetch — App state in
    // Components.js relies on its own useEffect chains so a custom event
    // is the lightest-touch nudge.
    try { window.dispatchEvent(new CustomEvent('edotask:workspace-changed', { detail: { id } })); } catch { /* noop */ }
  }, []);

  // When the active workspace disappears (e.g. the user was removed), fall
  // back to Personal automatically. Only triggers AFTER the org list has
  // loaded so we don't bounce out of an org mid-load on first paint.
  useEffect(() => {
    if (!orgsLoaded) return;
    if (activeId !== 'personal' && !workspaces.find((w) => w.id === activeId)) {
      switchWorkspace('personal');
    }
  }, [activeId, workspaces, switchWorkspace, orgsLoaded]);

  const value = useMemo(() => ({
    activeWorkspace,
    workspaces,
    switchWorkspace,
    refreshWorkspaces,
    isBusinessEnabled,
  }), [activeWorkspace, workspaces, switchWorkspace, refreshWorkspaces, isBusinessEnabled]);

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
};

export const useWorkspace = () => useContext(WorkspaceContext);

export default WorkspaceContext;
